import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { deleteOrderFile } from '../services/OrderService';
import { useToast } from '../context/UIContext';
import { nextOrderNumber } from '../utils/documentNumber';
import '../styles/Invoices.css';

const STATUS_OPTIONS = [
    { value: 'draft',     label: 'Entwurf' },
    { value: 'active',    label: 'Aktiv' },
    { value: 'completed', label: 'Abgeschlossen' },
    { value: 'cancelled', label: 'Storniert' },
];

const TODAY = new Date().toISOString().split('T')[0];

function emptyLine() {
    return { ticket_id: null, ticket_name: '', description: '', hours: 0 };
}

// `prefill` is a form draft for a *new* order — an accepted offer being converted. It stays a
// create: only the values come from elsewhere, so `isEdit` is still false.
export default function OrderModal({ order, allOrders, prefill = null, onClose, onSave }) {
    const isEdit = !!order;
    const isPrefilled = !isEdit && !!prefill;
    const toast = useToast();

    const [projects, setProjects]   = useState([]);
    const [companies, setCompanies] = useState([]);
    const [tickets, setTickets]     = useState([]);

    const [form, setForm] = useState({
        order_number: '',
        title: '',
        project_id: '',
        company_id: '',
        status: 'active',
        issue_date: TODAY,
        due_date: '',
        start_date: '',
        end_date: '',
        total_hours: '',
        notes: '',
        line_items: [],
    });

    useEffect(() => {
        if (order) {
            setForm({
                order_number: order.order_number || '',
                title:        order.title        || '',
                project_id:   order.project_id   ? String(order.project_id) : '',
                company_id:   order.company_id   ? String(order.company_id) : '',
                status:       order.status       || 'active',
                issue_date:   order.issue_date   || TODAY,
                due_date:     order.due_date      || '',
                start_date:   order.start_date    || '',
                end_date:     order.end_date      || '',
                total_hours:  order.total_hours != null ? String(order.total_hours) : '',
                notes:        order.notes         || '',
                line_items:   (order.line_items || []).map(li => ({ ...li })),
            });
        } else if (prefill) {
            setForm({
                order_number: prefill.order_number || nextOrderNumber(allOrders),
                title:        prefill.title        || '',
                // Left empty by the draft on purpose — the offer has no project.
                project_id:   prefill.project_id   ? String(prefill.project_id) : '',
                company_id:   prefill.company_id   ? String(prefill.company_id) : '',
                status:       prefill.status       || 'active',
                issue_date:   prefill.issue_date   || TODAY,
                due_date:     prefill.due_date     || '',
                start_date:   prefill.start_date   || '',
                end_date:     prefill.end_date     || '',
                total_hours:  prefill.total_hours != null ? String(prefill.total_hours) : '',
                notes:        prefill.notes        || '',
                line_items:   (prefill.line_items || []).map(li => ({ ...li })),
            });
        } else {
            setForm(f => ({ ...f, order_number: nextOrderNumber(allOrders) }));
        }
    }, [order, prefill]);

    useEffect(() => {
        api.get('/Project').then(r => setProjects(r.data || [])).catch(() => {});
        api.get('/Company').then(r => setCompanies(r.data || [])).catch(() => {});
    }, []);

    useEffect(() => {
        if (!form.project_id) { setTickets([]); return; }
        api.get(`/ProjectTickets/project/${form.project_id}`)
            .then(r => setTickets(r.data || []))
            .catch(() => setTickets([]));
    }, [form.project_id]);

    const set = (key, value) => setForm(f => ({ ...f, [key]: value }));

    // A new order's file can only be uploaded once it has an id, so it is held here and uploaded
    // by the page right after the order is created.
    const [pendingFile, setPendingFile] = useState(null);
    const [storedFileName, setStoredFileName] = useState(order?.file_name || null);

    const handleRemoveFile = async () => {
        if (pendingFile) { setPendingFile(null); return; }
        if (!order?.id || !storedFileName) return;
        try {
            await deleteOrderFile(order.id);
            setStoredFileName(null);
            toast('Datei entfernt.');
        } catch { toast('Datei konnte nicht entfernt werden.'); }
    };

    const addLine = () => setForm(f => ({ ...f, line_items: [...f.line_items, emptyLine()] }));
    const removeLine = (i) => setForm(f => ({ ...f, line_items: f.line_items.filter((_, idx) => idx !== i) }));
    const setLine = (i, key, value) =>
        setForm(f => ({
            ...f,
            line_items: f.line_items.map((li, idx) => {
                if (idx !== i) return li;
                const updated = { ...li, [key]: value };
                if (key === 'ticket_id') {
                    const t = tickets.find(t => String(t.id) === value);
                    updated.ticket_name = t ? t.title : '';
                }
                return updated;
            }),
        }));

    const lineTotal = form.line_items.reduce((s, li) => s + (parseFloat(li.hours) || 0), 0);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (form.start_date && form.end_date && form.end_date < form.start_date) {
            toast('Das Ende des Leistungszeitraums muss nach dem Beginn liegen.');
            return;
        }
        const payload = {
            order_number: form.order_number,
            title:        form.title,
            project_id:   form.project_id,
            company_id:   form.company_id || null,
            status:       form.status,
            issue_date:   form.issue_date,
            due_date:     form.due_date || null,
            start_date:   form.start_date || null,
            end_date:     form.end_date || null,
            total_hours:  form.line_items.length > 0
                ? lineTotal
                : parseFloat(form.total_hours) || 0,
            notes:        form.notes || null,
            line_items:   form.line_items.map(li => ({
                ticket_id:   li.ticket_id   || null,
                ticket_name: li.ticket_name || null,
                description: li.description || '',
                hours:       parseFloat(li.hours) || 0,
            })),
        };
        await onSave(payload, pendingFile);
    };

    return (
        <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
            <form className="inv-modal" onSubmit={handleSubmit} onClick={e => e.stopPropagation()}>

                <div className="inv-modal-header">
                    <div className="inv-modal-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"/>
                            <rect x="9" y="3" width="6" height="4" rx="1"/>
                            <path d="M9 12h6M9 16h4"/>
                        </svg>
                    </div>
                    <div>
                        <h2 className="inv-modal-title">{isEdit ? 'Auftrag bearbeiten' : 'Neuer Auftrag'}</h2>
                        <p className="inv-modal-sub">
                            {isPrefilled
                                ? 'Aus dem Angebot übernommen — Preise entfallen, nur Stunden zählen'
                                : 'Stundenkontingent für ein Projekt'}
                        </p>
                    </div>
                    <button type="button" className="inv-modal-close" onClick={onClose}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M18 6L6 18M6 6l12 12"/>
                        </svg>
                    </button>
                </div>

                <div className="inv-modal-body">

                    <p className="inv-section-title">Grunddaten</p>
                    <div className="inv-grid-3">
                        <div className="inv-field">
                            <label className="inv-label">Auftragsnummer *</label>
                            <input className="inv-input" value={form.order_number}
                                onChange={e => set('order_number', e.target.value)} required />
                        </div>
                        <div className="inv-field">
                            <label className="inv-label">Status</label>
                            <select className="inv-select" value={form.status}
                                onChange={e => set('status', e.target.value)}>
                                {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                        </div>
                        <div className="inv-field">
                            <label className="inv-label">Titel</label>
                            <input className="inv-input" value={form.title}
                                onChange={e => set('title', e.target.value)} />
                        </div>
                        <div className="inv-field">
                            <label className="inv-label">Auftragsdatum *</label>
                            <input className="inv-input" type="date" value={form.issue_date}
                                onChange={e => set('issue_date', e.target.value)} required />
                        </div>
                        <div className="inv-field">
                            <label className="inv-label">Fälligkeitsdatum</label>
                            <input className="inv-input" type="date" value={form.due_date}
                                onChange={e => set('due_date', e.target.value)} />
                        </div>
                    </div>

                    {/* Leistungszeitraum — von wann bis wann für den Kunden gearbeitet wird */}
                    <p className="inv-section-title">Leistungszeitraum</p>
                    <div className="inv-grid-2">
                        <div className="inv-field">
                            <label className="inv-label">Von</label>
                            <input className="inv-input" type="date" value={form.start_date}
                                onChange={e => set('start_date', e.target.value)} />
                        </div>
                        <div className="inv-field">
                            <label className="inv-label">Bis</label>
                            <input className="inv-input" type="date" value={form.end_date}
                                onChange={e => set('end_date', e.target.value)} />
                        </div>
                    </div>

                    <p className="inv-section-title">Projekt & Kunde</p>
                    <div className="inv-grid-2">
                        <div className="inv-field">
                            <label className="inv-label">Projekt *</label>
                            <select className="inv-select" value={form.project_id}
                                onChange={e => set('project_id', e.target.value)} required>
                                <option value="">– Projekt wählen –</option>
                                {projects.map(p => (
                                    <option key={p.id} value={String(p.id)}>{p.name}</option>
                                ))}
                            </select>
                            {isPrefilled && !form.project_id && (
                                <span className="ord-file-hint">
                                    Ein Angebot enthält kein Projekt — bitte auswählen.
                                </span>
                            )}
                        </div>
                        <div className="inv-field">
                            <label className="inv-label">Kunde</label>
                            <select className="inv-select" value={form.company_id}
                                onChange={e => set('company_id', e.target.value)}>
                                <option value="">– Kein Kunde –</option>
                                {companies.map(c => (
                                    <option key={c.id} value={String(c.id)}>{c.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <p className="inv-section-title">Stundenkontingent</p>

                    {form.line_items.length === 0 && (
                        <div className="inv-grid-2">
                            <div className="inv-field">
                                <label className="inv-label">Gesamtstunden *</label>
                                <input className="inv-input" type="number" min="0" step="0.5"
                                    value={form.total_hours}
                                    onChange={e => set('total_hours', e.target.value)}
                                    placeholder="z.B. 80" />
                            </div>
                        </div>
                    )}

                    {form.line_items.length > 0 && (
                        <div style={{ marginBottom: 'var(--space-2)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                            Gesamt: <strong>{lineTotal} h</strong> (aus Positionen)
                        </div>
                    )}

                    <div className="inv-items-header">
                        <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            Positionen (Ticket-Aufteilung)
                        </span>
                        <button type="button" className="inv-btn" onClick={addLine}>+ Position</button>
                    </div>

                    {form.line_items.length > 0 && (
                        <table className="inv-items-table">
                            <thead>
                                <tr>
                                    <th style={{ width: '28%' }}>Ticket</th>
                                    <th>Beschreibung</th>
                                    <th style={{ width: '90px' }}>Stunden</th>
                                    <th style={{ width: '32px' }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {form.line_items.map((li, i) => (
                                    <tr key={i}>
                                        <td>
                                            <select
                                                className="inv-item-input"
                                                value={li.ticket_id || ''}
                                                onChange={e => setLine(i, 'ticket_id', e.target.value || null)}
                                            >
                                                <option value="">– Kein Ticket –</option>
                                                {tickets.map(t => (
                                                    <option key={t.id} value={String(t.id)}>{t.title}</option>
                                                ))}
                                            </select>
                                        </td>
                                        <td>
                                            <input
                                                className="inv-item-input"
                                                value={li.description}
                                                onChange={e => setLine(i, 'description', e.target.value)}
                                                placeholder="Beschreibung"
                                            />
                                        </td>
                                        <td>
                                            <input
                                                className="inv-item-input"
                                                type="number" min="0" step="0.5"
                                                value={li.hours}
                                                onChange={e => setLine(i, 'hours', e.target.value)}
                                                style={{ textAlign: 'right' }}
                                            />
                                        </td>
                                        <td>
                                            <button type="button" className="inv-item-del" onClick={() => removeLine(i)}>
                                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M18 6L6 18M6 6l12 12"/>
                                                </svg>
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}

                    <p className="inv-section-title">Datei</p>
                    <div className="ord-file-row">
                        {(pendingFile || storedFileName) ? (
                            <>
                                <span className="ord-file-name" title={pendingFile?.name || storedFileName}>
                                    📎 {pendingFile?.name || storedFileName}
                                    {pendingFile && <em className="ord-file-hint"> — wird beim Speichern hochgeladen</em>}
                                </span>
                                <button type="button" className="inv-btn danger" onClick={handleRemoveFile}>
                                    Entfernen
                                </button>
                            </>
                        ) : (
                            <>
                                <input
                                    className="inv-input"
                                    type="file"
                                    accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                                    onChange={e => setPendingFile(e.target.files?.[0] || null)}
                                />
                                <span className="ord-file-hint">PDF, Word, Excel oder Bild</span>
                            </>
                        )}
                    </div>

                    <p className="inv-section-title">Notizen</p>
                    <div className="inv-field">
                        <textarea className="inv-textarea" value={form.notes}
                            onChange={e => set('notes', e.target.value)}
                            placeholder="Interne Notizen zum Auftrag" rows={3} />
                    </div>

                </div>

                <div className="inv-modal-footer">
                    <button type="button" className="inv-btn" onClick={onClose}>Abbrechen</button>
                    <button type="submit" className="inv-btn primary">
                        {isEdit ? 'Speichern' : 'Auftrag erstellen'}
                    </button>
                </div>

            </form>
        </div>
    );
}
